import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Link, useNavigate } from "@tanstack/react-router"
import { Loader2, Send, ArrowLeft, ShieldCheck, Lock, Truck, Activity } from "lucide-react"
import { z } from "zod"
import { useForgotPassword } from "@/hooks/auth/useForgotPassword"
import { Button } from "@/components/ui/button"
import { useDevice } from "@/hooks/useIsMobile"

import { emailSchema } from "@/lib/schemas/auth.schema"

const forgotPasswordSchema = z.object({
  email: emailSchema,
})

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>

import { AuthLeftPanel } from "./AuthLeftPanel"

export function ForgotPasswordForm() {
  const { isDesktop } = useDevice()
  const navigate = useNavigate()
  const forgotPassMutation = useForgotPassword()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: "onTouched",
    defaultValues: { email: "" },
  })

  const onSubmit = (values: ForgotPasswordFormValues) => {
    forgotPassMutation.mutate(values.email, {
      onSuccess: () => {
        // Store User Email in localStorage
        localStorage.setItem("reset_email", values.email)
        // Redirect to Reset Password page
        navigate({ to: "/reset-password" })
      },
    })
  }

  return (
    <div className="min-h-[100dvh] lg:min-h-screen flex flex-col lg:flex-row w-full bg-[#f5f5f5]">
      
      {/* Left Panel: Branding & Benefits */}
      {isDesktop && <AuthLeftPanel />}

      {/* Right Panel: Forgot Password Form */}
      <main className="w-full lg:w-1/2 bg-[#f5f5f5] flex flex-col h-[100dvh] lg:h-screen p-4 sm:p-6 overflow-y-auto">
        {/* Top-Left Back Navigation (Positioned at the top of the page on Mobile & Tablet, Hidden on Desktop) */}
        <div className="w-full lg:hidden shrink-0">
          <Link
            to="/login"
            className="group inline-flex items-center gap-1.5 text-xs font-semibold text-secondary hover:text-[#be001e] transition-colors duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#be001e]/30 rounded-md p-1"
          >
            <ArrowLeft className="size-3.5 transition-transform duration-200 group-hover:-translate-x-1" />
            <span>Back to Login</span>
          </Link>
        </div>

        {/* Card Container (Centered with space between top navigation and card) */}
        <div className="w-full max-w-[380px] mx-auto my-auto py-6 pb-20 sm:pb-8 lg:py-0">
          <div className="bg-white w-full rounded-xl p-6 border-[0.5px] border-outline-variant flex flex-col gap-4.5 shadow-sm">
          <header className="flex flex-col gap-1.5">
            {/* <Link
              to="/login"
              className="hidden lg:inline-flex items-center gap-1 text-[11px] font-bold text-secondary hover:text-[#be001e] transition-colors w-fit"
            >
              <ArrowLeft className="size-3" /> Back to Login
            </Link> */}
            <h1 className="font-sans text-xl font-black text-on-surface">Forgot Password</h1>
            <p className="text-xs text-secondary font-medium">
              Enter your email address and we'll send you an OTP to reset your password.
            </p>
          </header>

          {/* Error Message */}
          {forgotPassMutation.isError && (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-1.5 text-xs text-destructive font-medium">
              {(forgotPassMutation.error as any)?.response?.data?.message || forgotPassMutation.error.message || "Failed to submit request"}
            </p>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-secondary" htmlFor="forgot-email">
                Email address
              </label>
              <input
                id="forgot-email"
                type="email"
                placeholder="name@gmail.com"
                autoComplete="email"
                aria-invalid={!!errors.email}
                className="w-full h-9 px-3 border-[0.5px] border-outline-variant rounded-lg text-xs bg-surface text-on-surface focus:outline-none focus:border-primary focus:ring-0"
                {...register("email")}
              />
              {errors.email && (
                <p className="text-[11px] text-primary font-medium">{errors.email.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={forgotPassMutation.isPending}
              className="w-full h-9 bg-primary-container text-on-primary-container font-semibold rounded-lg text-xs transition-all duration-200 active:scale-[0.98] flex items-center justify-center hover:bg-primary-container/95 cursor-pointer mt-1"
            >
              {forgotPassMutation.isPending ? (
                <Loader2 className="size-3.5 mr-1.5 animate-spin" />
              ) : (
                <Send className="size-3.5 mr-1.5" />
              )}
              {forgotPassMutation.isPending ? "Sending OTP…" : "Send Reset Code"}
            </Button>
          </form>
        </div>
      </div>
    </main>

    </div>
  )
}
